<template>
    <div class="edit-client-form">
        <div class="form-header">
            <h2>Edit Client Information</h2>
            <p class="text-muted">Update details for {{ formData.fullname_client }}</p>
        </div>

        <form @submit.prevent="handleSubmit">
            <!-- Personal Information Section -->
            <div class="form-section">
                <h3>
                    <span class="section-icon">👤</span>
                    Personal Information
                </h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>Full Name <span class="required">*</span></label>
                        <input type="text" v-model="formData.fullname_client" class="form-control"
                            :class="{ 'error': errors.fullname_client }" placeholder="Enter full name" />
                        <span v-if="errors.fullname_client" class="error-text">{{ errors.fullname_client }}</span>
                    </div>

                    <div class="form-group">
                        <label>Username <span class="required">*</span></label>
                        <input type="text" v-model="formData.username_client" class="form-control"
                            :class="{ 'error': errors.username_client }" placeholder="Enter username" />
                        <span v-if="errors.username_client" class="error-text">{{ errors.username_client }}</span>
                    </div>

                    <div class="form-group">
                        <label>Mobile Number</label>
                        <input type="tel" v-model="formData.mobileno_client" class="form-control"
                            placeholder="Enter mobile number" />
                    </div>

                    <div class="form-group">
                        <label>Profile Plan <span class="required">*</span></label>
                        <select v-model="formData.profile_client" class="form-control"
                            :class="{ 'error': errors.profile_client }">
                            <option value="">Select Plan</option>
                            <option value="5Mbps">5 Mbps</option>
                            <option value="10Mbps">10 Mbps</option>
                            <option value="15Mbps">15 Mbps</option>
                            <option value="20Mbps">20 Mbps</option>
                            <option value="25Mbps">25 Mbps</option>
                            <option value="30Mbps">30 Mbps</option>
                            <option value="35Mbps">35 Mbps</option>
                            <option value="40Mbps">40 Mbps</option>
                            <option value="50Mbps">50 Mbps</option>
                            <option value="70Mbps">70 Mbps</option>
                        </select>
                        <span v-if="errors.profile_client" class="error-text">{{ errors.profile_client }}</span>
                    </div>
                </div>
            </div>

            <!-- Account Settings Section -->
            <div class="form-section">
                <h3>
                    <span class="section-icon">⚙️</span>
                    Account Settings
                </h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>Monthly Due (₱) <span class="required">*</span></label>
                        <input type="number" v-model="formData.monthlydue_client" step="0.01" min="0"
                            class="form-control" :class="{ 'error': errors.monthlydue_client }" placeholder="0.00" />
                        <span v-if="errors.monthlydue_client" class="error-text">{{ errors.monthlydue_client }}</span>
                    </div>

                    <div class="form-group">
                        <label>Balance (₱)</label>
                        <input type="number" v-model="formData.balance_client" step="0.01" class="form-control"
                            placeholder="0.00" />
                    </div>

                    <div class="form-group">
                        <label>Due Day</label>
                        <select v-model="formData.due_client" class="form-control">
                            <option value="">Select Day</option>
                            <option v-for="day in 31" :key="day" :value="day">{{ day }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label>Auto-disconnect</label>
                        <div class="toggle-switch">
                            <label class="switch">
                                <input type="checkbox" v-model="formData.autodisconnect_client" :true-value="1"
                                    :false-value="0" />
                                <span class="slider round"></span>
                            </label>
                            <span class="toggle-label">{{ formData.autodisconnect_client ? 'Enabled' : 'Disabled'
                                }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Location & Network Section -->
            <div class="form-section">
                <h3>
                    <span class="section-icon">📍</span>
                    Location & Network
                </h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>Barangay</label>
                        <input type="text" v-model="formData.brgy_client" class="form-control"
                            placeholder="Enter barangay" />
                    </div>

                    <div class="form-group">
                        <label>Sitio</label>
                        <input type="text" v-model="formData.sitio_client" class="form-control"
                            placeholder="Enter sitio" />
                    </div>

                    <div class="form-group">
                        <label>NAP</label>
                        <input type="text" v-model="formData.nap_client" class="form-control" placeholder="Enter NAP" />
                    </div>

                    <div class="form-group">
                        <label>Port</label>
                        <input type="text" v-model="formData.port_client" class="form-control"
                            placeholder="Enter port" />
                    </div>

                    <div class="form-group">
                        <label>Latitude</label>
                        <input type="text" v-model="formData.lat_client" class="form-control"
                            placeholder="e.g., 11.02144592" />
                    </div>

                    <div class="form-group">
                        <label>Longitude</label>
                        <input type="text" v-model="formData.lng_client" class="form-control"
                            placeholder="e.g., 124.45983823" />
                    </div>
                </div>
            </div>

            <!-- Additional Information Section -->
            <div class="form-section">
                <h3>
                    <span class="section-icon">📝</span>
                    Additional Information
                </h3>
                <div class="form-grid">
                    <div class="form-group full-width">
                        <label>Date Installed</label>
                        <input type="text" v-model="formData.dateinstalled_client" class="form-control" rows="3"
                            placeholder="Enter date installed MM/dd/yyyy" />
                    </div>
                    <div class="form-group full-width">
                        <label>Description</label>
                        <textarea v-model="formData.description_client" class="form-control" rows="3"
                            placeholder="Enter description"></textarea>
                    </div>

                    <div class="form-group full-width">
                        <label>Remarks</label>
                        <textarea v-model="formData.remarks_client" class="form-control" rows="2"
                            placeholder="Enter remarks"></textarea>
                    </div>
                </div>
            </div>

            <!-- Status Toggles Section -->
            <div class="form-section">
                <h3>
                    <span class="section-icon">⚡</span>
                    Status Settings
                </h3>
                <div class="status-toggles">
                    <div class="status-item">
                        <label class="toggle-label">Active Status</label>
                        <div class="toggle-switch">
                            <label class="switch">
                                <input type="checkbox" v-model="formData.isactive_client" :true-value="1"
                                    :false-value="0" />
                                <span class="slider round"></span>
                            </label>
                            <span class="toggle-label">{{ formData.isactive_client ? 'Active' : 'Inactive' }}</span>
                        </div>
                    </div>

                    <div class="status-item">
                        <label class="toggle-label">Billed Status</label>
                        <div class="toggle-switch">
                            <label class="switch">
                                <input type="checkbox" v-model="formData.isbilled_client" :true-value="1"
                                    :false-value="0" />
                                <span class="slider round"></span>
                            </label>
                            <span class="toggle-label">{{ formData.isbilled_client ? 'Billed' : 'Unbilled' }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Read-only Information -->
            <div class="form-section read-only-section">
                <h3>
                    <span class="section-icon">ℹ️</span>
                    System Information
                </h3>
                <div class="info-grid read-only">
                    <div class="info-item">
                        <span class="label">Client ID:</span>
                        <span class="value">{{ originalData.userpk_client }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Date Added:</span>
                        <span class="value">{{ Formatters.formatDateTime(originalData.dateadded_client) }}</span>
                    </div>
                    <div class="info-item" v-if="originalData.dateinstalled_client">
                        <span class="label">Date Installed:</span>
                        <span class="value">{{ Formatters.formatDateTime(originalData.dateinstalled_client) }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Last Logout:</span>
                        <span class="value">{{ Formatters.formatDateTime(originalData.lastlogoutdate_client) }}</span>
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="form-actions">
                <button type="button" class="btn btn-secondary" @click="handleCancel">
                    Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                    <span v-if="isSubmitting" class="spinner"></span>
                    {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import Formatters from '@/utils/formatters';
import clientService from '@/services/clientService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import confirmationService from '@/services/confirmationService';

// eslint-disable-next-line
const emit = defineEmits(['save-success', 'cancel', 'update-success']);

// Form state
const formData = reactive({
    syspk_client: 0,
    fullname_client: '',
    username_client: '',
    mobileno_client: '',
    profile_client: '',
    dateinstalled_client: '',
    monthlydue_client: 0,
    balance_client: 0,
    due_client: null,
    autodisconnect_client: 1,
    isactive_client: 1,
    isbilled_client: 1,
    brgy_client: '',
    sitio_client: '',
    nap_client: '',
    port_client: '',
    lat_client: '',
    lng_client: '',
    description_client: '',
    remarks_client: ''
});

// Store original data for read-only fields and comparison
const originalData = ref({});

// Form state
const isSubmitting = ref(false);
const errors = ref({});

// Initialize form with client data
const initForm = (client) => {
    if (client) {
        originalData.value = { ...client };

        Object.keys(formData).forEach(key => {
            if (client[key] !== undefined) {
                formData[key] = client[key];
            }
        });
    }
};


// Validation rules
const validateForm = () => {
    const newErrors = {};

    if (!formData.fullname_client?.trim()) {
        newErrors.fullname_client = 'Full name is required';
    }

    if (!formData.username_client?.trim()) {
        newErrors.username_client = 'Username is required';
    }

    if (!formData.profile_client) {
        newErrors.profile_client = 'Profile plan is required';
    }

    if (!formData.monthlydue_client || formData.monthlydue_client <= 0) {
        newErrors.monthlydue_client = 'Monthly due must be greater than 0';
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};



// Handlers
const handleSubmit = async () => {
    if (!validateForm()) return;
    const confirmed = await confirmationService.show("Are you sure you want to proceed?");
    if (!confirmed)
        return;
    isSubmitting.value = true;

    // Prepare data for submission
    const updatedData = {
        ...formData,
        // Ensure boolean values are properly set
        isactive_client: Number(formData.isactive_client),
        isbilled_client: Number(formData.isbilled_client),
        autodisconnect_client: Number(formData.autodisconnect_client),
        bIsactive_client: formData.profile_client !== originalData.value.profile_client
    };


    try {
        // await emit('save', updatedData);
        if (updatedData.syspk_client > 0) {
            spinnerService.show();
            const resp = await clientService.update(updatedData);
            if (resp.success) {
                toastService.success('Client updated successfully!');
                emit('update-success', updatedData)
            }
            else {
                toastService.error(resp.message);
            }
        }
    } finally {
        isSubmitting.value = false;
        spinnerService.hide();
    }
};

const handleCancel = () => {
    emit('cancel');
};
// eslint-disable-next-line
const handleReset = () => {
    initForm();
};

const setClient = (client) => {
    originalData.value = { ...client };

    Object.keys(formData).forEach(key => {
        if (client[key] !== undefined) {
            formData[key] = client[key];
        }
    });
}
// eslint-disable-next-line
defineExpose({ setClient })
</script>

<style scoped>
.edit-client-form {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.form-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #E5E7EB;
}

.form-header h2 {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
}

.form-header p {
    color: #6B7280;
    margin: 0;
}

.form-section {
    margin-bottom: 32px;
    padding: 20px;
    background: #F9FAFB;
    border-radius: 12px;
    border: 1px solid #E5E7EB;
}

.form-section h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 20px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #E5E7EB;
}

.section-icon {
    font-size: 20px;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.form-group.full-width {
    grid-column: 1 / -1;
}

label {
    font-size: 13px;
    font-weight: 500;
    color: #4B5563;
}

.required {
    color: #DC2626;
    margin-left: 2px;
}

.form-control {
    padding: 10px 12px;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s;
    background: white;
}

.form-control:focus {
    outline: none;
    border-color: #0A5C9E;
    box-shadow: 0 0 0 3px rgba(10, 92, 158, 0.1);
}

.form-control.error {
    border-color: #DC2626;
}

.form-control.error:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

textarea.form-control {
    resize: vertical;
    min-height: 80px;
}

.error-text {
    font-size: 11px;
    color: #DC2626;
}

/* Toggle Switch Styles */
.toggle-switch {
    display: flex;
    align-items: center;
    gap: 12px;
}

.switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
    margin: 0;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #E5E7EB;
    transition: .3s;
}

.slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .3s;
}

input:checked+.slider {
    background-color: #0A5C9E;
}

input:focus+.slider {
    box-shadow: 0 0 1px #0A5C9E;
}

input:checked+.slider:before {
    transform: translateX(26px);
}

.slider.round {
    border-radius: 24px;
}

.slider.round:before {
    border-radius: 50%;
}

.toggle-label {
    font-size: 14px;
    color: #4B5563;
}

/* Status Toggles */
.status-toggles {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
}

.status-item {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 200px;
}

.status-item .toggle-label {
    min-width: 100px;
}

/* Read-only Section */
.read-only-section {
    background: #F3F4F6;
}

.info-grid.read-only {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-item .label {
    font-size: 11px;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.info-item .value {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
}

/* Form Actions */
.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    padding-top: 20px;
    border-top: 2px solid #E5E7EB;
}

.btn {
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-primary {
    background: #0A5C9E;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #084A7D;
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: #F3F4F6;
    color: #4B5563;
    border: 1px solid #D1D5DB;
}

.btn-secondary:hover {
    background: #E5E7EB;
}

/* Spinner */
.spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
    margin-right: 8px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive */
@media (max-width: 640px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .status-toggles {
        flex-direction: column;
        gap: 16px;
    }

    .form-actions {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
    }
}

/* Bootstrap compatibility */
:deep(.form-control) {
    box-shadow: none;
}

:deep(.form-control:focus) {
    box-shadow: 0 0 0 3px rgba(10, 92, 158, 0.1);
}
</style>